import { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { http } from '@/api/http';
import { ApiVersion } from '@/config';

enum RNBGLEvent {
  BOOT = 'boot',
  TERMINATE = 'terminate',
  LOCATION = 'location',
  MOTION_CHANGE = 'motionchange',
  HTTP = 'http',
  HEARTBEAT = 'heartbeat',
  PROVIDER_CHANGE = 'providerchange',
  ACTIVITY_CHANGE = 'activitychange',
  GEOFENCE = 'geofence',
  GEOFENCE_CHANGE = 'geofencechange',
  ENABLED_CHANGE = 'enabledchange',
  CONNECTIVITY_CHANGE = 'connectivitychange',
  SCHEDULE = 'schedule',
  POWER_SAVE_CHANGE = 'powersavechange',
  NOTIFICATION_ACTION = 'notificationaction',
}

enum RNBGLActivityType {
  STILL = 'still',
  ON_FOOT = 'on_foot',
  WALKING = 'walking',
  RUNNING = 'running',
  IN_VEHICLE = 'in_vehicle',
  ON_BICYCLE = 'on_bicycle',
  UNKNOWN = 'unknown',
}

interface BuildLocationParams {
  latitude: number;
  longitude: number;
  distance: number;
  cfsId?: string | null;
  unitShiftId: string;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: string;
}

export interface ICoords {
  latitude: number;
  longitude: number;
  accuracy: number;
  speed: number;
  speed_accuracy: number;
  heading: number;
  heading_accuracy: number;
  altitude: number;
  ellipsoidal_altitude: number;
  altitude_accuracy: number | null;
}

export interface IActivity {
  type: RNBGLActivityType;
  confidence: number;
}

export interface IBattery {
  level: number;
  is_charging: boolean;
}

export interface IExtras {
  cfs?: string;
  unitShift?: string;
}

export interface ILocationPayload {
  uuid: string;
  timestamp: string;
  age: number;
  event: RNBGLEvent;
  is_moving: boolean;
  is_heartbeat: boolean;
  odometer: number;
  mock: boolean;
  coords: ICoords;
  geofence?: any;
  activity: IActivity;
  battery: IBattery;
  extras: IExtras;
  platform: string;
  version: string;
  deviceUID: string;
}

export const buildLocationPayload = ({
  latitude,
  longitude,
  distance,
  cfsId,
  unitShiftId,
  timestamp,
  speed,
  heading,
  altitudeAccuracy,
  altitude,
  accuracy,
}: BuildLocationParams) => {
  return {
    uuid: uuidv4(),
    timestamp,
    age: 0,
    event: RNBGLEvent.MOTION_CHANGE,
    is_moving: true,
    is_heartbeat: false,
    odometer: distance,
    mock: false,
    coords: {
      latitude: latitude || 0,
      longitude: longitude || 0,
      accuracy: accuracy || 0,
      speed: speed || 0,
      speed_accuracy: 0,
      heading: heading || 0,
      heading_accuracy: 0,
      altitude: altitude || 0,
      ellipsoidal_altitude: 0,
      altitude_accuracy: altitudeAccuracy || 0,
    },
    geofence: undefined,
    activity: {
      type: RNBGLActivityType.UNKNOWN,
      confidence: 100,
    },
    battery: {
      level: 1.0,
      is_charging: true,
    },
    extras: {
      ...(cfsId && { cfs: cfsId }),
      unitShiftId,
    },
  };
};

export const LocationApi = {
  sendLocation: (location: ILocationPayload): Promise<AxiosResponse<any>> => {
    return http.post(
      `api/${ApiVersion.V1}/locations`,
      { location },
      {
        headers: {
          'X-Platform': location.platform,
          'X-App-Version': location.version,
          'X-DEVICE-UID': location.deviceUID,
        },
      },
    );
  },
};
